'use client';

import Image from 'next/image';
import { useEffect, useRef } from 'react';

import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import { gsap } from '@/lib/gsap';

import css from './style.module.scss';
import { FoundersDto } from './types';

interface Props {
	data: FoundersDto;
}

const Founders = ({ data }: Props) => {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const imageRef = useRef<HTMLDivElement>(null);
	const titleRef = useRef<HTMLHeadingElement>(null);
	const cardsRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const wrapper = wrapperRef.current;
		const title = titleRef.current;
		const titleLines = title?.querySelectorAll('p');

		if (!wrapper || !title || !titleLines) return;

		const ctx = gsap.context(() => {
			const matchMedia = gsap.matchMedia();

			matchMedia.add(
				{
					isDesktop: `(min-width: 992px)`,
					isMobile: `(max-width: 991px)`
				},
				context => {
					const { isMobile } = context.conditions as gsap.Conditions;

					if (isMobile) return;

					gsap.set(titleLines, {
						opacity: 0,
						y: 40,
						filter: 'blur(10px)'
					});

					gsap.to(titleLines, {
						opacity: 1,
						y: 0,
						filter: 'blur(0px)',
						duration: 1.1,
						ease: 'power3.out',
						stagger: 0.15,
						scrollTrigger: {
							trigger: titleLines,
							start: 'top 80%',
							toggleActions: 'play none none reverse'
						}
					});

					gsap.to(title, {
						yPercent: -50,
						ease: 'none',
						scrollTrigger: {
							trigger: wrapper,
							start: 'top bottom',
							end: 'bottom top',
							scrub: true
						}
					});

					gsap.to(cardsRef.current, {
						yPercent: 170,
						ease: 'none',
						scrollTrigger: {
							trigger: wrapper,
							start: 'top bottom',
							end: 'bottom top',
							scrub: true
						}
					});

					gsap.fromTo(
						cardsRef.current,
						{ autoAlpha: 0, y: 80 },
						{
							autoAlpha: 1,
							y: 0,
							duration: 1.2,
							delay: 0.2,
							ease: 'power3.out',
							scrollTrigger: {
								trigger: wrapper,
								start: 'top 70%'
							}
						}
					);
				}
			);
		}, wrapperRef);

		return () => ctx.revert();
	}, []);

	return (
		<section ref={wrapperRef} className={css.section}>
			<div ref={imageRef} className={css.imageWrapper}>
				<Image
					height={3200}
					width={1600}
					src="/assets/images/about-us-page/founders.webp"
					alt="Qream Founders"
					className={css.image}
				/>
			</div>

			<div className={css.overlay} />

			<div className={css.inner}>
				<Typography ref={titleRef} uppercase className={css.title} variant="h2" tag="h2">
					<StrapiRichContent content={data.title} />
				</Typography>

				<div ref={cardsRef} className={css.cardsBlock}>
					{data.cards.map((card, index) => (
						<div key={index} className={css.card}>
							<Typography uppercase className={css.cardTitle} variant="h4" tag="h3">
								{card.name}
							</Typography>
							<p>{card.role}</p>
							<div className={css.glass}>{card.description}</div>
						</div>
					))}
				</div>
			</div>
		</section>
	);
};

export default Founders;
